// Recordings of calls and meetings this person was in (admins: all). Played in the page, downloaded, or deleted by
// whoever recorded it, the meeting's host or an admin.
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatDuration } from "../../lib/calls";
import { formatDay, formatSize, formatTime } from "../../lib/chat";
import { useLiveEvent } from "../../lib/live";
import { Alert, Badge, Button, Card, Spinner } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";

type Recording = {
  id: string; title: string; status: "starting" | "recording" | "processing" | "ready" | "failed"; audioOnly: boolean;
  startedAt: string; endedAt: string | null; durationSeconds: number | null; sizeBytes: number | null; startedBy: string | null;
  source: "meeting" | "call"; conversationId: string | null; canDelete: boolean;
};

const STATUS: Record<Recording["status"], { text: string; tone: "slate" | "brand" | "amber" | "red" }> = {
  starting: { text: "Starting…", tone: "amber" }, recording: { text: "Recording", tone: "red" }, processing: { text: "Processing…", tone: "amber" },
  ready: { text: "Ready", tone: "brand" }, failed: { text: "Failed", tone: "red" },
};

export function RecordingsPage() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ["recordings", org.slug];
  const list = useQuery({
    queryKey: key, queryFn: () => api<{ recordings: Recording[] }>(`/orgs/${org.slug}/recordings`),
    refetchInterval: (q) => (q.state.data?.recordings.some((r) => r.status !== "ready" && r.status !== "failed") ? 5000 : false),
  });
  useLiveEvent((e) => { if (e.type === "recording.ready" || e.type === "call.recording") void qc.invalidateQueries({ queryKey: key }); });
  const file = (r: Recording, download = false) => `/api/v1/orgs/${org.slug}/recordings/${r.id}/file${download ? "?download=1" : ""}`;

  async function remove(r: Recording) {
    if (!confirm(`Delete the recording "${r.title}" for everyone? This can't be undone.`)) return;
    setError(null);
    try { await api(`/orgs/${org.slug}/recordings/${r.id}`, { method: "DELETE" }); await qc.invalidateQueries({ queryKey: key }); } catch (err) { setError(errorText(err)); }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6"><h1 className="text-2xl font-semibold tracking-tight">Recordings</h1><p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Calls and meetings you were in that someone recorded. Start a recording with Record during a call or meeting.</p></div>
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <Card className="p-0">
        {list.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {list.data?.recordings.length === 0 && (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="record" /></div>
            <p className="font-medium">No recordings yet</p>
            <p className="mt-1 text-sm text-slate-500">Recordings appear here once a recorded call or meeting finishes.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {list.data?.recordings.map((r) => {
            const s = STATUS[r.status];
            return (
              <li key={r.id} className="px-5 py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name={r.source === "meeting" ? "video" : r.audioOnly ? "phone" : "video"} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 font-medium">{r.title}<Badge tone={s.tone}>{s.text}</Badge></p>
                    <p className="text-sm text-slate-500">
                      {formatDay(r.startedAt)}, {formatTime(r.startedAt)}{r.durationSeconds ? ` · ${formatDuration(r.durationSeconds)}` : ""}{r.sizeBytes ? ` · ${formatSize(r.sizeBytes)}` : ""}{r.startedBy ? ` · recorded by ${r.startedBy}` : ""}
                      {r.source === "call" && r.conversationId && <> · <Link to={`/o/${org.slug}/chat/${r.conversationId}`} className="text-brand-600 hover:underline">open chat</Link></>}
                    </p>
                  </div>
                  {r.status === "ready" && (
                    <div className="flex gap-1">
                      <Button size="sm" variant={playing === r.id ? "primary" : "secondary"} onClick={() => setPlaying(playing === r.id ? null : r.id)}><Icon name="play" className="h-4 w-4" />{playing === r.id ? "Close" : "Play"}</Button>
                      <a href={file(r, true)}><Button size="sm" variant="ghost"><Icon name="download" className="h-4 w-4" />Download</Button></a>
                    </div>
                  )}
                  {r.canDelete && r.status !== "starting" && r.status !== "recording" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r)}>Delete</Button>}
                </div>
                {playing === r.id && (
                  <div className="mt-4 overflow-hidden rounded-xl bg-black">
                    {r.audioOnly
                      ? <audio controls autoPlay src={file(r)} className="w-full" />
                      : <video controls autoPlay preload="metadata" src={file(r)} className="max-h-[70vh] w-full" />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
